import { DEFAULT_MANAGER_IMAGE_FEMALE, DEFAULT_MANAGER_IMAGE_MALE } from "@/types/manager.definition";
import { DEFAULT_SECRETARY_IMAGE_FEMALE, DEFAULT_SECRETARY_IMAGE_MALE } from "@/types/secretary.definition";
import { CivilityEnum, RoleEnum } from "@/types/user.definition";
import {
  Bell,
  Building2,
  ChevronDown,
  Menu,
  Settings,
  User,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFacilityUser from "../../hooks/useFacilityUser";
import useManager from "../../hooks/useManager";
import { scrollToTop } from "../../hooks/useScrollToTop";
import {
  fetchNotificationsRequest,
  markAllAsReadRequest,
  markAsReadRequest,
} from "../../store/reducers/notificationSlice";
import { requestTerminateSessionRequest } from "../../store/reducers/sessionSlice";
import { RootState } from "../../store/store";
import { AppNotification } from "../../types/notification.definition";
import {
  formatNotificationTime,
  getNotificationText,
  getNotificationTypeIcon,
} from "../../utils/notification-utils";

interface TopNavigationProps {
  onMenuClick?: () => void;
}

const getTypeColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    appointment: "primary",
    payment: "success",
    licence: "warning",
  };
  return colorMap[type] || "primary";
};

export const TopNavigation: React.FC<TopNavigationProps> = ({
  onMenuClick,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const manager = useManager();
  const facilityUser = useFacilityUser();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const { notifications, unreadCount, loading } = useSelector(
    (state: RootState) => state.notifications,
  );

  // Refs for click outside detection
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  let defaultImage = DEFAULT_MANAGER_IMAGE_MALE;

  if (facilityUser?.roleName === RoleEnum.MANAGER) {
    defaultImage =
      facilityUser?.civility === CivilityEnum.MR
        ? DEFAULT_MANAGER_IMAGE_MALE
        : DEFAULT_MANAGER_IMAGE_FEMALE;
  }

  if (facilityUser?.roleName === RoleEnum.SECRETARY) {
    defaultImage =
      facilityUser?.civility === CivilityEnum.MR
        ? DEFAULT_SECRETARY_IMAGE_MALE
        : DEFAULT_SECRETARY_IMAGE_FEMALE;
  }


  // Fetch real notifications on mount
  useEffect(() => {
    dispatch(fetchNotificationsRequest());
  }, [dispatch]);

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNotifications]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  const handleNavigation = (path: string) => {
    navigate(path);
    scrollToTop();
  };

  const handleNotificationClick = (notification: AppNotification) => {
    if (!notification.isRead) {
      dispatch(markAsReadRequest(notification.id));
    }
    setShowNotifications(false);
    handleNavigation("/manager/notifications");
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsReadRequest());
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    // Dispatch terminate session action - saga handles API call and redirect
    dispatch(requestTerminateSessionRequest());
  };

  const preview = notifications.slice(0, 5);

  return (
    <header
      className="px-4 py-3 bg-white border-b border-neutral-200"
      data-testid="dashboard-header"
    >
      <div className="flex justify-between items-center">
        {/* Left side - Logo and Mobile Menu */}
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg transition-colors lg:hidden hover:bg-neutral-100"
          >
            <Menu className="w-5 h-5 text-neutral-600" />
          </button>

          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-primary">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-neutral-900">
                {facilityUser?.healthcareFacility?.name}
              </h1>
              <p className="text-xs text-neutral-500">
                {t(`common.facilityManagement`)}
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Notifications and User Menu */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={`relative p-2.5 rounded-xl transition-all duration-200 hover:bg-neutral-100 hover:shadow-md group ${showNotifications ? "bg-neutral-100 shadow-md" : ""
                }`}
            >
              <Bell
                className={`w-5 h-5 transition-all duration-200 ${showNotifications
                  ? "scale-110 text-primary-600"
                  : "text-neutral-600 group-hover:text-primary-600 group-hover:scale-105"
                  }`}
              />

              {/* Unread badge */}
              {unreadCount > 0 && (
                <span className="flex absolute -top-1 -right-1 justify-center items-center min-w-[20px] h-5 px-1 text-xs font-bold text-white rounded-full bg-red-500 shadow-lg border-2 border-white animate-pulse">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}

              <div className="absolute inset-0 rounded-xl opacity-0 transition-opacity duration-200 bg-primary-500 group-hover:opacity-10"></div>
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 z-[100] mt-3 w-80 bg-white rounded-2xl border shadow-xl transition-all duration-200 transform border-neutral-200 animate-in slide-in-from-top-2">
                <div className="p-5 border-b border-neutral-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-neutral-900">
                        {t("notifications.title")}
                      </h3>
                      <p className="mt-1 text-sm text-neutral-600">
                        {t("notifications.unreadCount", {
                          count: unreadCount,
                        })}
                      </p>
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="px-3 py-1 text-xs font-semibold rounded-lg transition-colors text-primary-600 hover:text-primary-700 hover:bg-ews-primary-50"
                      >
                        {t("notifications.markAllAsRead")}
                      </button>
                    )}
                  </div>
                </div>

                <div className="overflow-y-auto max-h-96">
                  <div className="py-2">
                    {loading ? (
                      <div className="px-5 py-8 text-center">
                        <div className="inline-block w-6 h-6 rounded-full border-4 animate-spin border-primary-600 border-t-transparent"></div>
                        <p className="mt-2 text-sm text-neutral-600">
                          {t("notifications.loading")}
                        </p>
                      </div>
                    ) : preview.length === 0 ? (
                      <div className="px-5 py-8 text-center">
                        <Bell className="mx-auto w-8 h-8 text-neutral-400" />
                        <p className="mt-2 text-sm text-neutral-600">
                          {t("notifications.noNotifications")}
                        </p>
                      </div>
                    ) : (
                      preview.map((notification, index) => {
                        const IconComponent = getNotificationTypeIcon(notification.type);
                        const color = getTypeColor(notification.type);
                        const { title, message } = getNotificationText(
                          notification,
                          t,
                          i18n.language,
                        );
                        return (
                          <div
                            key={notification.id}
                            className={`px-5 py-4 hover:bg-neutral-50 transition-all duration-200 cursor-pointer ${!notification.isRead
                              ? "bg-ews-primary-50/50 border-l-4 border-primary-500"
                              : ""
                              }`}
                            onClick={() =>
                              handleNotificationClick(notification)
                            }
                            style={{ animationDelay: `${index * 50}ms` }}
                          >
                            <div className="flex items-start space-x-3">
                              <div
                                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color === "error"
                                  ? "bg-error-100"
                                  : color === "warning"
                                    ? "bg-warning-100"
                                    : color === "success"
                                      ? "bg-success-100"
                                      : "bg-ews-primary-100"
                                  }`}
                              >
                                <IconComponent
                                  className={`w-5 h-5 ${color === "error"
                                    ? "text-error-600"
                                    : color === "warning"
                                      ? "text-warning-600"
                                      : color === "success"
                                        ? "text-success-600"
                                        : "text-primary-600"
                                    }`}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center mb-1 space-x-2">
                                  <p className="text-sm font-semibold truncate text-neutral-900">
                                    {title}
                                  </p>
                                  {!notification.isRead && (
                                    <div className="flex-shrink-0 w-2 h-2 rounded-full animate-pulse bg-primary-500"></div>
                                  )}
                                </div>
                                <p className="mb-1 text-sm truncate text-neutral-600">
                                  {message}
                                </p>
                                <p className="text-xs text-neutral-400">
                                  {formatNotificationTime(
                                    notification.createdAt,
                                    t,
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-neutral-100">
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      handleNavigation("/manager/notifications");
                    }}
                    className="px-3 py-2 w-full text-sm font-semibold rounded-lg transition-colors duration-200 text-primary-600 hover:text-primary-700 hover:bg-ews-primary-50"
                  >
                    {unreadCount > 0
                      ? t("notifications.viewAllCount", { count: unreadCount })
                      : t("notifications.viewAll")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center p-2 space-x-3 rounded-lg transition-colors hover:bg-neutral-100"
            >
              <img
                src={
                  manager?.photoUrl || defaultImage
                }
                alt="Profile"
                className="object-cover w-8 h-8 rounded-full"
              />
              <div className="hidden text-left sm:block">
                <p className="text-sm font-medium text-neutral-900">
                  {manager?.firstName} {manager?.lastName}
                </p>
                <p className="text-xs text-neutral-500">
                  {t(`common.${manager?.roleName?.toUpperCase()}`)}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-neutral-600" />
            </button>

            {/* User Menu Dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 z-[100] mt-2 w-48 bg-white rounded-xl border shadow-soft border-neutral-200">
                <div className="py-1">
                  <button
                    className="flex items-center px-4 py-2 space-x-3 w-full text-sm transition-colors duration-200 text-neutral-700 hover:bg-neutral-50"
                    onClick={() => handleNavigation("/profile")}
                  >
                    <User className="w-4 h-4" />
                    <span>{t("header.profile")}</span>
                  </button>
                  <button
                    className="flex items-center px-4 py-2 space-x-3 w-full text-sm transition-colors duration-200 text-neutral-700 hover:bg-neutral-50"
                    onClick={() => handleNavigation("/user-settings")}
                  >
                    <Settings className="w-4 h-4" />
                    <span>{t("header.settings")}</span>
                  </button>
                  <hr className="my-1 border-neutral-200" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center px-4 py-2 space-x-3 w-full text-sm transition-colors duration-200 text-error-600 hover:bg-error-50"
                  >
                    <User className="w-4 h-4" />
                    <span>{t("sidebar.logout")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
